import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlanKey, SchoolDetailDto, SchoolModulesResponse } from '@eskoolhub/types';
import { PageHeader } from '../components/AppShell';
import { SchoolAdmins } from '../features/SchoolAdmins';
import { ModuleBoard, moduleSummary, type BoardModule } from '../components/ModuleBoard';
import { useToast } from '../components/Toasts';
import { ErrorState, Modal, SchoolStatusChip, SkeletonRows, SkeletonTiles } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatDate, formatINR, formatINRCompact, formatNumber, formatRate } from '../lib/format';
import {
  PricingFields,
  draftToPaise,
  paiseToRupeeString,
  type PricingDraft,
} from '../features/PricingFields';

export function SchoolDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [suspending, setSuspending] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [changingPlan, setChangingPlan] = useState(false);
  const [editingPricing, setEditingPricing] = useState(false);
  const [pricingDraft, setPricingDraft] = useState<PricingDraft | null>(null);
  const [studentsDraft, setStudentsDraft] = useState('');
  const [draft, setDraft] = useState<Set<string> | null>(null);

  const school = useQuery({
    queryKey: ['school', id],
    queryFn: () => api.get<SchoolDetailDto>(`/api/schools/${id}`),
  });

  const modulesQuery = useQuery({
    queryKey: ['school-modules', id],
    queryFn: () => api.get<SchoolModulesResponse>(`/api/schools/${id}/modules`),
  });

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () =>
      api.get<{
        plans: {
          key: PlanKey;
          name: string;
          tagline: string;
          pricePerStudentPerYearPaise: number | null;
          pricePerStudentPerMonthPaise: number | null;
        }[];
      }>('/api/billing/plans'),
  });

  const [brandOpen, setBrandOpen] = useState(false);
  const [brand, setBrand] = useState({ primaryColor: '#1FA97A', logoUrl: '' });

  const saveBranding = useMutation({
    mutationFn: () =>
      api.patch<SchoolDetailDto>(`/api/schools/${id}/branding`, {
        primaryColor: brand.primaryColor,
        // Empty means "take it down", which is a different instruction from
        // "leave it alone" — so it is sent as null rather than omitted.
        logoUrl: brand.logoUrl.trim() || null,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setBrandOpen(false);
      toast.push('Branding saved', 'success');
    },
    onError: (error) => toast.push(error instanceof Error ? error.message : 'Could not save', 'error'),
  });

  const savePricing = useMutation({
    mutationFn: (input: { paise: number | null; students: number; note: string }) =>
      api.patch<SchoolDetailDto>(`/api/schools/${id}/pricing`, {
        pricePerStudentPerMonthPaise: input.paise,
        billableStudents: input.students,
        note: input.note || undefined,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setEditingPricing(false);
      setPricingDraft(null);
      toast.push('Pricing updated', 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not save pricing', 'error'),
  });

  const modules: BoardModule[] = useMemo(
    () =>
      (modulesQuery.data?.modules ?? []).map((m) => ({
        key: m.key,
        name: m.name,
        description: m.description,
        category: m.category,
        isCore: m.isCore,
        requires: m.requires,
        planDefault: m.planDefault,
      })),
    [modulesQuery.data],
  );

  const saved = useMemo(
    () => new Set((modulesQuery.data?.modules ?? []).filter((m) => m.enabled).map((m) => m.key)),
    [modulesQuery.data],
  );

  const selected = draft ?? saved;
  const dirty = draft !== null && (draft.size !== saved.size || [...draft].some((key) => !saved.has(key)));

  const saveModules = useMutation({
    mutationFn: async (next: Set<string>) => {
      const changes = modules
        .filter((m) => !m.isCore && next.has(m.key) !== saved.has(m.key))
        .map((m) => ({ key: m.key, enabled: next.has(m.key) }));
      return api.patch<SchoolModulesResponse>(`/api/schools/${id}/modules`, { modules: changes });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['school-modules', id], result);
      void queryClient.invalidateQueries({ queryKey: ['school', id] });
      setDraft(null);
      toast.push('Features updated for this school', 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not save features', 'error'),
  });

  const suspend = useMutation({
    mutationFn: () => api.post<SchoolDetailDto>(`/api/schools/${id}/suspend`, { reason: suspendReason }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setSuspending(false);
      setSuspendReason('');
      toast.push(`${updated.name} suspended`, 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not suspend', 'error'),
  });

  const reactivate = useMutation({
    mutationFn: () => api.post<SchoolDetailDto>(`/api/schools/${id}/reactivate`),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      toast.push(`${updated.name} reactivated`, 'success');
    },
  });

  const changePlan = useMutation({
    mutationFn: (planKey: PlanKey) => api.post<SchoolDetailDto>(`/api/schools/${id}/plan`, { planKey }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['school', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['school-modules', id] });
      void queryClient.invalidateQueries({ queryKey: ['schools'] });
      setChangingPlan(false);
      setDraft(null);
      toast.push(`Plan changed to ${updated.planName}`, 'success');
    },
    onError: (error) => toast.push(error instanceof ApiRequestError ? error.message : 'Could not change plan', 'error'),
  });

  if (school.error) {
    return (
      <>
        <PageHeader title="School" />
        <div className="page">
          <ErrorState
            message={school.error instanceof ApiRequestError ? school.error.message : 'Could not load this school'}
            onRetry={() => void school.refetch()}
          />
        </div>
      </>
    );
  }

  if (school.isPending) {
    return (
      <>
        <PageHeader title="Loading…" />
        <div className="page">
          <SkeletonTiles count={5} />
          <SkeletonRows />
        </div>
      </>
    );
  }

  const data = school.data;

  function openPricingEditor() {
    const pricing = school.data?.pricing;
    setPricingDraft({
      custom: pricing?.isCustom ?? false,
      rupees: paiseToRupeeString(pricing?.isCustom ? pricing.effectiveMonthlyPaise : null),
      note: pricing?.note ?? '',
    });
    setStudentsDraft(String(pricing?.billableStudents ?? 0));
    setEditingPricing(true);
  }

  return (
    <>
      <PageHeader
        title={data.name}
        meta={`School ID · ${data.reference} · ${data.city}, ${data.state} · join code ${data.code}`}
        actions={
          <>
            <button className="btn btn--ghost" onClick={() => navigate('/schools')}>
              All schools
            </button>
            <SchoolStatusChip status={data.status} trialDaysLeft={data.trialDaysLeft} />
          </>
        }
      />

      <div className="page">
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            <Fact label="Plan" value={`${data.planName ?? '—'}${data.billingCycle ? ` · ${data.billingCycle.toLowerCase()}` : ''}`} />
            <Fact label="Students" value={formatNumber(data.studentCount)} mono />
            <Fact label="Admins" value={formatNumber(data.adminCount)} mono />
            <Fact label="Renewal" value={formatDate(data.renewsAt)} mono />
            <Fact label="Support tier" value={data.supportTier.charAt(0) + data.supportTier.slice(1).toLowerCase()} mono />
          </div>

          <div style={{ display: 'flex', gap: 10, borderTop: '1px solid var(--rule)', paddingTop: 16, flexWrap: 'wrap' }}>
            <button className="btn btn--soft" onClick={() => toast.push('Impersonation lands with the School Admin surface', 'info')}>
              Log in as admin
            </button>
            <button className="btn btn--soft" onClick={() => setChangingPlan(true)}>
              Change plan
            </button>
            <button className="btn btn--soft" onClick={() => openPricingEditor()}>
              Edit pricing
            </button>
            <button className="btn btn--soft" onClick={() => navigate(`/billing?school=${data.id}`)}>
              View invoices
            </button>
            {data.status === 'SUSPENDED' ? (
              <button
                className="btn btn--soft"
                style={{ marginLeft: 'auto', color: 'var(--leaf)' }}
                onClick={() => reactivate.mutate()}
                disabled={reactivate.isPending}
              >
                Reactivate
              </button>
            ) : (
              <button className="btn btn--danger" style={{ marginLeft: 'auto' }} onClick={() => setSuspending(true)}>
                Suspend
              </button>
            )}
          </div>
        </div>

        <SchoolAdmins schoolId={id!} />

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div className="card__title">Branding</div>
            {data.primaryColor || data.logoUrl ? null : (
              <span className="tag">Using platform defaults</span>
            )}
            <button
              className="btn btn--soft btn--sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => {
                setBrand({
                  primaryColor: data.primaryColor ?? '#1FA97A',
                  logoUrl: data.logoUrl ?? '',
                });
                setBrandOpen(true);
              }}
            >
              Edit branding
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                aria-hidden="true"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: data.primaryColor ?? 'var(--ink-tint)',
                  border: '1px solid var(--rule)',
                  flex: 'none',
                }}
              />
              <Fact label="Brand colour" value={data.primaryColor ?? 'Not set'} mono />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {data.logoUrl ? (
                <img
                  src={data.logoUrl}
                  alt=""
                  style={{
                    height: 42,
                    width: 42,
                    objectFit: 'contain',
                    borderRadius: 12,
                    border: '1px solid var(--rule)',
                    background: 'var(--white)',
                    flex: 'none',
                  }}
                />
              ) : (
                <div className="school-tile" style={{ height: 42, width: 42 }}>
                  {data.initials}
                </div>
              )}
              <Fact label="Logo" value={data.logoUrl ? 'Uploaded' : 'Initials'} />
            </div>
          </div>

          <div className="caption">
            Used on this school's login screen and through the parent app. Changing
            them here takes effect the next time an app loads — no rebuild needed.
          </div>
        </div>

        {data.pricing ? (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div className="card__title">Pricing</div>
              {data.pricing.isCustom ? (
                <span className="tag tag--manual">Negotiated rate</span>
              ) : (
                <span className="tag">{data.planName} list price</span>
              )}
              <button className="btn btn--soft btn--sm" style={{ marginLeft: 'auto' }} onClick={() => openPricingEditor()}>
                Edit pricing
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
              <Fact
                label="Per student / month"
                value={data.pricing.effectiveMonthlyPaise === null ? 'Not set' : formatRate(data.pricing.effectiveMonthlyPaise)}
                mono
              />
              <Fact label="Billable students" value={formatNumber(data.pricing.billableStudents)} mono />
              <Fact label="Per month" value={formatINR(Math.round(data.pricing.annualAmountPaise / 12))} mono />
              <Fact label="Per year" value={formatINR(data.pricing.annualAmountPaise)} mono />
              <Fact
                label={data.billingCycle === 'ANNUAL' ? 'Each invoice · annual' : 'Each invoice · monthly'}
                value={formatINR(data.pricing.cycleAmountPaise)}
                mono
              />
            </div>

            {data.pricing.isCustom ? (
              <div className="caption" style={{ borderTop: '1px solid var(--rule)', paddingTop: 12 }}>
                {data.pricing.planListMonthlyPaise !== null ? (
                  <>
                    List price is {formatRate(data.pricing.planListMonthlyPaise)}/student/month
                    {data.pricing.discountBps !== null ? (
                      <>
                        {' '}
                        —{' '}
                        <strong style={{ color: data.pricing.discountBps < 0 ? 'var(--leaf)' : 'var(--marigold-deep)' }}>
                          {data.pricing.discountBps < 0
                            ? `${Math.abs(data.pricing.discountBps / 100).toFixed(1)}% discount`
                            : `${(data.pricing.discountBps / 100).toFixed(1)}% above list`}
                        </strong>
                      </>
                    ) : null}
                    .
                  </>
                ) : (
                  <>{data.planName} has no list price; this school is priced per deal.</>
                )}
                {data.pricing.note ? ` ${data.pricing.note}` : ''}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid-4">
          <StatCard label="MRR" value={formatINRCompact(data.mrrPaise)} caption={data.billingCycle === 'ANNUAL' ? 'annual contract ÷ 12' : 'billed monthly'} />
          <StatCard label="Active parents" value={formatNumber(data.activeParentCount)} caption={`${Math.round((data.activeParentCount / Math.max(data.studentCount, 1)) * 100)}% of students`} />
          <StatCard label="Board" value={data.board} caption={`Onboarded ${formatDate(data.onboardedAt)}`} />
          <StatCard label="Features on" value={`${selected.size + modules.filter((m) => m.isCore && !selected.has(m.key)).length}`} caption={`of ${modules.length} modules`} />
        </div>

        {/* Per-school feature provisioning */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div className="card__title">Features</div>
            <span className="caption">{modules.length > 0 ? moduleSummary(modules, selected) : ''}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
              {dirty ? (
                <>
                  <button className="btn btn--soft" onClick={() => setDraft(null)} disabled={saveModules.isPending}>
                    Discard
                  </button>
                  <button className="btn btn--sm" onClick={() => saveModules.mutate(selected)} disabled={saveModules.isPending}>
                    {saveModules.isPending ? 'Saving…' : 'Save changes'}
                  </button>
                </>
              ) : (
                <span className="caption">
                  Inherited from the {modulesQuery.data?.planKey ?? '—'} plan; overrides are kept if the plan changes.
                </span>
              )}
            </div>
          </div>

          {modulesQuery.isPending ? (
            <SkeletonRows count={3} />
          ) : (
            <ModuleBoard modules={modules} selected={selected} onChange={setDraft} />
          )}
        </div>
      </div>

      {brandOpen ? (
        <Modal
          title="Branding"
          onClose={() => setBrandOpen(false)}
          footer={
            <>
              <button className="btn" disabled={saveBranding.isPending} onClick={() => saveBranding.mutate()}>
                {saveBranding.isPending ? 'Saving…' : 'Save branding'}
              </button>
              <button className="btn btn--ghost" onClick={() => setBrandOpen(false)}>
                Cancel
              </button>
            </>
          }
        >
          <div className="field">
            <span>Brand colour</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="color"
                aria-label="Brand colour"
                value={brand.primaryColor}
                onChange={(e) => setBrand((b) => ({ ...b, primaryColor: e.target.value }))}
                style={{
                  width: 44,
                  height: 36,
                  padding: 2,
                  border: '1px solid var(--rule)',
                  borderRadius: 8,
                  background: 'var(--white)',
                  cursor: 'pointer',
                  flex: 'none',
                }}
              />
              <input
                value={brand.primaryColor}
                onChange={(e) => setBrand((b) => ({ ...b, primaryColor: e.target.value }))}
                placeholder="#1FA97A"
                spellCheck={false}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </div>
          </div>

          <label className="field">
            <span>Logo URL</span>
            <input
              value={brand.logoUrl}
              onChange={(e) => setBrand((b) => ({ ...b, logoUrl: e.target.value }))}
              placeholder="https://greenfield.edu.in/logo.png"
              spellCheck={false}
            />
            <span className="field__hint">
              Clear the box to go back to the school's initials.
            </span>
          </label>
        </Modal>
      ) : null}

      {suspending ? (
        <Modal
          title={`Suspend ${data.name}?`}
          onClose={() => setSuspending(false)}
          footer={
            <>
              <button className="btn btn--ghost" onClick={() => setSuspending(false)}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ marginLeft: 'auto', background: 'var(--coral)', color: '#fff' }}
                onClick={() => suspend.mutate()}
                disabled={suspendReason.trim().length < 3 || suspend.isPending}
              >
                {suspend.isPending ? 'Suspending…' : 'Suspend school'}
              </button>
            </>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            Staff and parents lose access immediately. Data is retained and the school can be reactivated. This is
            recorded in the audit log against your account.
          </p>
          <label className="field">
            <span>Reason</span>
            <textarea
              rows={3}
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Non-payment after three reminders"
            />
          </label>
        </Modal>
      ) : null}

      {editingPricing && pricingDraft ? (
        <Modal
          title="Pricing for this school"
          onClose={() => setEditingPricing(false)}
          wide
          footer={
            <>
              <button className="btn btn--ghost" onClick={() => setEditingPricing(false)} disabled={savePricing.isPending}>
                Cancel
              </button>
              <button
                className="btn"
                style={{ marginLeft: 'auto' }}
                disabled={
                  savePricing.isPending ||
                  (pricingDraft.custom && draftToPaise(pricingDraft) === null) ||
                  (data.pricing?.planListMonthlyPaise === null && draftToPaise(pricingDraft) === null)
                }
                onClick={() =>
                  savePricing.mutate({
                    paise: draftToPaise(pricingDraft),
                    students: Number(studentsDraft) || 0,
                    note: pricingDraft.note.trim(),
                  })
                }
              >
                {savePricing.isPending ? 'Saving…' : 'Save pricing'}
              </button>
            </>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            Changing this re-prices the next invoice, not ones already issued. The change is recorded in the audit
            log against your account.
          </p>

          <label className="field" style={{ maxWidth: 220 }}>
            <span>Billable students</span>
            <input
              inputMode="numeric"
              value={studentsDraft}
              onChange={(e) => setStudentsDraft(e.target.value.replace(/\D/g, ''))}
            />
          </label>

          <PricingFields
            draft={pricingDraft}
            onChange={setPricingDraft}
            planName={data.planName ?? 'This plan'}
            planListMonthlyPaise={data.pricing?.planListMonthlyPaise ?? null}
            students={Number(studentsDraft) || 0}
            billingCycle={data.billingCycle ?? 'ANNUAL'}
          />
        </Modal>
      ) : null}

      {changingPlan ? (
        <Modal
          title="Change plan"
          onClose={() => setChangingPlan(false)}
          footer={
            <button className="btn btn--ghost" onClick={() => setChangingPlan(false)}>
              Cancel
            </button>
          }
        >
          <p className="caption" style={{ margin: 0 }}>
            Features you set by hand for this school are kept. Everything else follows the new plan.
            {data.pricing?.isCustom ? (
              <>
                {' '}
                This school's negotiated rate of{' '}
                <strong>{formatRate(data.pricing.effectiveMonthlyPaise ?? 0)}/student/month</strong> is also kept —
                use <em>Edit pricing</em> afterwards to move it to the new list price.
              </>
            ) : null}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(plans.data?.plans ?? []).map((plan) => (
              <button
                key={plan.key}
                className="module-row"
                style={{ cursor: 'pointer', textAlign: 'left', borderColor: plan.key === data.planKey ? 'var(--marigold)' : undefined }}
                onClick={() => changePlan.mutate(plan.key)}
                disabled={changePlan.isPending}
              >
                <div style={{ flex: 1 }}>
                  <div className="module-row__name">
                    {plan.name}
                    {plan.key === data.planKey ? <span className="tag">Current</span> : null}
                  </div>
                  <div className="module-row__desc">{plan.tagline}</div>
                </div>
                <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
                  {plan.pricePerStudentPerMonthPaise
                    ? `${formatRate(plan.pricePerStudentPerMonthPaise)}/student/mo`
                    : 'Custom'}
                </span>
              </button>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span className="label" style={{ fontSize: 11, letterSpacing: '0.08em' }}>
        {label}
      </span>
      <span className={mono ? 'mono' : ''} style={{ fontSize: 14.5, fontWeight: mono ? 600 : 700 }}>
        {value}
      </span>
    </div>
  );
}

function StatCard({ label, value, caption }: { label: string; value: string; caption: string }) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div className="tile__value" style={{ fontSize: 26 }}>
        {value}
      </div>
      <div className="caption">{caption}</div>
    </div>
  );
}
